// Central store backed by FastAPI + MongoDB.
// Mantém a mesma API: useStore() → [state, update]
// Os componentes não precisam de saber que agora há backend.

import { useEffect, useState } from "react";
import { api } from "./api";

const DEFAULT_TCO_TABLE = {
    escaloes: [0, 10, 25, 40, 55, 75],
    tcos: ["<80%", "80%", "90%", "100%", "110%"],
    values: {
        0: { "<80%": 4.22, "80%": 4.65, "90%": 4.82, "100%": 5.0, "110%": 5.35 },
        10: { "<80%": 4.32, "80%": 4.75, "90%": 4.93, "100%": 5.11, "110%": 5.47 },
        25: { "<80%": 4.44, "80%": 4.88, "90%": 5.07, "100%": 5.25, "110%": 5.62 },
        40: { "<80%": 4.56, "80%": 5.02, "90%": 5.21, "100%": 5.4, "110%": 5.78 },
        55: { "<80%": 4.68, "80%": 5.15, "90%": 5.34, "100%": 5.54, "110%": 5.93 },
        75: { "<80%": 4.8, "80%": 5.28, "90%": 5.48, "100%": 5.68, "110%": 6.08 },
    },
};

const DEFAULT_DESPESAS_FIXAS = [];
const DEFAULT_VPPS = [];

export const PLANOS = [
    "M1 TV",
    "M1 Net",
    "M2 Net+Móvel",
    "M2 TV+Net 2ª Hab",
    "M3",
    "M4",
    "Energia",
    "Móvel",
];

// Helpers: para contar TV / Energia / tipo no breakdown do painel
export const planoContaTV = (p) =>
    ["M1 TV", "M2 TV+Net 2ª Hab", "M3", "M4"].includes(p);
export const planoContaEnergia = (p) => p === "Energia";
export const ZONAS = ["Norte", "Centro", "Sul"];
export const FONTE_TIPOS = ["Influenciadores", "Lead Influencer", "Relocation", "Instagram", "Indicação", "Outros"];

// Mapeia valores legacy → valor canónico actual.
// "Leads Parceiro" foi renomeado para "Lead Influencer" em Jun/2026
// porque era confuso com a tabela de Parceiros (entidade diferente).
const LEGACY_FONTE_MAP = { "Leads Parceiro": "Lead Influencer" };
const normalizeFonte = (t) => LEGACY_FONTE_MAP[t] || t;
export const normalizeVenda = (v) => ({
    ...v,
    fonteTipo: normalizeFonte(v.fonteTipo),
});
export const PORTABILIDADE_VALUE = 12;

const DEFAULT = {
    tcoTable: DEFAULT_TCO_TABLE,
    tcoAtual: "100%",
    escalaoAtual: 10,
    // mesesConfig: { "YYYY-MM": { escalao: 10, tco: "100%" } }
    // Cada mês tem o SEU escalão+TCO independente. Ao mudar no Configurações,
    // edita-se APENAS o mês actual. Meses anteriores ficam congelados.
    // Fallback (mês sem entrada) usa os globais tcoAtual/escalaoAtual.
    mesesConfig: {},
    metaCreditos: 3000,
    despesasFixas: DEFAULT_DESPESAS_FIXAS,
    despesasExtras: [],
    vendas: [],
    vpps: DEFAULT_VPPS,
    parceirosRelocation: [],
    incentivosUsados: [],
    alertasDispensados: [],
    historicoAlertas: [],
    privateNumbers: [],
    suporteAccessFrom: null,
    // FASE: Escalão Automático.
    // Quando true, o escalão de cada mês NÃO fechado é calculado em tempo real
    // com base no count (nTV + nEnergia ajustado). O TCO mantém-se manual.
    // Meses fechados ignoram esta flag (respeitam sempre o snapshot).
    autoEscalao: true,
    // Lista de nomes usados em "Lead Influencer" (auto-cresce ao registar vendas).
    // Permite reutilizar em autocomplete sem ter de digitar tudo de novo.
    leadsInfluencer: [],
};

// ============ STORAGE LAYER (API-backed) ============

let _state = { ...DEFAULT };
let _booted = false;
let _booting = null;
const _subs = new Set();

const _emit = () => _subs.forEach((fn) => fn(_state));

const _setState = (next) => {
    _state = next;
    _emit();
};

// Fields que vão no PUT /api/config (tudo excepto vendas)
const CONFIG_FIELDS = [
    "tcoTable",
    "tcoAtual",
    "escalaoAtual",
    "mesesConfig",
    "metaCreditos",
    "despesasFixas",
    "despesasExtras",
    "vpps",
    "parceirosRelocation",
    "incentivosUsados",
    "alertasDispensados",
    "historicoAlertas",
    "privateNumbers",
    "suporteAccessFrom",
    "autoEscalao",
    "leadsInfluencer",
];

const pickConfig = (s) => {
    const out = {};
    for (const k of CONFIG_FIELDS) out[k] = s[k];
    return out;
};

// Detecta diferenças em vendas e dispara API
const syncVendasDiff = async (oldVendas, newVendas) => {
    const oldMap = new Map((oldVendas || []).map((v) => [v.id, v]));
    const newMap = new Map((newVendas || []).map((v) => [v.id, v]));
    const tasks = [];
    // create or update
    for (const [id, v] of newMap.entries()) {
        const prev = oldMap.get(id);
        if (!prev) {
            // POST: backend devolve a venda completa (com kommoLeadId, urlKommo, etc preenchidos pelo Kommo sync)
            tasks.push(
                (async () => {
                    const { data } = await api.post("/vendas", v);
                    _state = {
                        ..._state,
                        vendas: _state.vendas.map((x) =>
                            x.id === id ? { ...data } : x,
                        ),
                    };
                    _emit();
                })(),
            );
        } else if (JSON.stringify(prev) !== JSON.stringify(v)) {
            tasks.push(api.put(`/vendas/${id}`, v));
        }
    }
    // deletes
    for (const id of oldMap.keys()) {
        if (!newMap.has(id)) tasks.push(api.delete(`/vendas/${id}`));
    }
    await Promise.allSettled(tasks);
};

let _configSyncTimer = null;
const scheduleConfigSync = () => {
    // PROTECÇÃO ANTI-PERDA-DE-DADOS:
    // Não sincronizar antes do bootstrap estar 100% completo. Se GET /config falhou
    // no boot, o store está em DEFAULT (listas vazias) e fazer PUT aqui apagava
    // dados reais do servidor. Esperamos o boot completar com sucesso primeiro.
    if (!_booted) {
        console.warn("[store] scheduleConfigSync ignorado: boot incompleto");
        return;
    }
    clearTimeout(_configSyncTimer);
    _configSyncTimer = setTimeout(async () => {
        try {
            await api.put("/config", pickConfig(_state));
        } catch (e) {
            // O backend pode recusar (409) escritas destrutivas. Nesse caso, recarregar
            // tudo do servidor para corrigir o estado local divergente.
            if (e?.response?.status === 409) {
                console.error("[store] PUT /config recusado (escrita destrutiva). Recarregando do servidor:", e?.response?.data);
                try {
                    await refreshAll();
                } catch (_) { /* offline, mantém estado actual */ }
                return;
            }
            // silencioso — colaborador não pode editar config
            // refazer fetch para ficar igual ao servidor
            try {
                await refreshAll();
            } catch (_) { /* offline, mantém estado actual */ }
        }
    }, 350);
};

export const refreshAll = async () => {
    const [cfg, vendas] = await Promise.all([
        api.get("/config").then((r) => r.data),
        api.get("/vendas").then((r) => r.data),
    ]);
    _setState({
        ...DEFAULT,
        ...cfg,
        vendas: (vendas || []).map(normalizeVenda),
    });
    if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("vendas:changed"));
};

export const bootstrapStore = async () => {
    if (_booted) return _state;
    if (_booting) return _booting;
    _booting = (async () => {
        try {
            // Retry simples com backoff: se falhar, tenta 3 vezes antes de desistir.
            // Importante: se NUNCA conseguir, _booted fica false → scheduleConfigSync
            // bloqueia escritas (evita apagar dados reais por erro de rede).
            let lastErr = null;
            for (let attempt = 1; attempt <= 3; attempt++) {
                try {
                    await refreshAll();
                    lastErr = null;
                    break;
                } catch (e) {
                    lastErr = e;
                    if (attempt < 3) {
                        await new Promise((r) => setTimeout(r, attempt * 1000));
                    }
                }
            }
            if (lastErr) {
                console.error("[store] bootstrap falhou após 3 tentativas — escritas bloqueadas até reconectar", lastErr);
                throw lastErr;
            }
            _booted = true;
            // Polling adaptativo:
            //   - tab activa: refresh a cada 60s (sincroniza mudanças de outros dispositivos)
            //   - tab inactiva (em background): pausa o polling para economizar bateria/rede
            //   - quando o user volta à tab → refresca IMEDIATAMENTE (não há sensação de "stale")
            let pollHandle = null;
            const startPoll = () => {
                if (pollHandle) return;
                pollHandle = setInterval(() => {
                    if (document.visibilityState === "visible") {
                        refreshAll().catch(() => {});
                    }
                }, 60000);
            };
            const stopPoll = () => {
                if (pollHandle) { clearInterval(pollHandle); pollHandle = null; }
            };
            startPoll();
            // Refresh imediato sempre que a tab volta a ficar activa
            document.addEventListener("visibilitychange", () => {
                if (document.visibilityState === "visible") {
                    refreshAll().catch(() => {});
                    startPoll();
                } else {
                    stopPoll();
                }
            });
            // Quando recupera ligação após estar offline → refresh imediato
            window.addEventListener("online", () => {
                refreshAll().catch(() => {});
            });
        } catch (e) {
            // Bootstrap falhou: tenta de novo na próxima chamada (não congela _booting=true)
            _booting = null;
        }
        return _state;
    })();
    return _booting;
};

export const useStore = () => {
    const [s, setS] = useState(_state);
    useEffect(() => {
        const sub = (next) => setS(next);
        _subs.add(sub);
        // Garante boot se ainda não foi feito
        bootstrapStore();
        return () => {
            _subs.delete(sub);
        };
    }, []);

    const update = (patch) => {
        const oldState = _state;
        const next =
            typeof patch === "function" ? patch(oldState) : { ...oldState, ...patch };

        // Optimistic update local
        _setState(next);

        // Sync vendas se mudaram (referência diferente)
        if (next.vendas !== oldState.vendas) {
            syncVendasDiff(oldState.vendas, next.vendas).catch(() => {
                refreshAll().catch(() => {});
            });
        }

        // Sync config se algum campo de config mudou
        let configChanged = false;
        for (const k of CONFIG_FIELDS) {
            if (next[k] !== oldState[k]) {
                configChanged = true;
                break;
            }
        }
        if (configChanged) scheduleConfigSync();
    };

    return [s, update];
};

// Atualização LOCAL APENAS de uma venda — não dispara PUT redundante para o backend.
// Usar quando o componente já fez o PATCH e só precisa de refletir a mudança no store.
export const patchVendaLocal = (vendaId, patch) => {
    if (!vendaId || !patch) return;
    _state = {
        ..._state,
        vendas: (_state.vendas || []).map((v) =>
            v.id === vendaId ? { ...v, ...patch } : v,
        ),
    };
    _emit();
};

// "Repor dados" — admin only, faz reset da config (não apaga vendas)
export const resetStore = async () => {
    try {
        await api.put("/config", pickConfig(DEFAULT));
        await refreshAll();
    } catch (e) {
        // colaborador não tem permissão, ignora
    }
};

// ============ DERIVED ============

// Helper para chave de mês "YYYY-MM"
const _monthKey = (date = new Date()) => {
    const d = (date instanceof Date) ? date : new Date(date);
    if (isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// Devolve {escalao, tco} para um mês específico. Fallback aos globais
// (s.escalaoAtual / s.tcoAtual) se o mês ainda não tem config própria.
//
// Auto-escalão: quando s.autoEscalao=true E o mês NÃO está fechado,
// o escalão é derivado em tempo real de (nTV + nEnergia) — sobrepondo
// qualquer valor manual em mesesConfig. O TCO mantém-se manual.
export const getEscalaoTcoForMonth = (s, monthKey) => {
    const entry = (s.mesesConfig || {})[monthKey];
    // Mês fechado tem prioridade absoluta (respeita o snapshot)
    if (entry?.fechado && entry.escalao !== undefined && entry.tco !== undefined) {
        return { escalao: entry.escalao, tco: entry.tco };
    }
    // TCO manual (ou do snapshot/fallback)
    const tco =
        entry && entry.tco !== undefined ? entry.tco : s.tcoAtual;
    // Escalão: auto (se flag ON) OU manual
    if (s.autoEscalao && monthKey) {
        const auto = computeAutoEscalao(s, monthKey);
        return { escalao: auto, tco };
    }
    if (entry && entry.escalao !== undefined) {
        return { escalao: entry.escalao, tco };
    }
    return { escalao: s.escalaoAtual, tco };
};

// Devolve a meta mensal (créditos) para um mês específico. Se o mês tem
// `meta` em `mesesConfig`, usa-a — senão faz fallback ao global `s.metaCreditos`.
// Assim editar a meta enquanto se está a ver Julho não afecta Agosto.
export const getMetaForMonth = (s, monthKey) => {
    const entry = (s.mesesConfig || {})[monthKey];
    if (entry && entry.meta !== undefined && entry.meta !== null && entry.meta !== "") {
        return Number(entry.meta) || 0;
    }
    return Number(s.metaCreditos) || 0;
};

// Conta TV + Energia (ajustado por cancelamentos) para um mês.
// Mesma lógica do kpisDoMes mas SEM chamar kpisDoMes para evitar recursão
// (kpisDoMes → valorPorCredito → getEscalaoTcoForMonth → aqui).
export const countTVMaisEnergiaNoMes = (s, ymKey) => {
    const vendasVenda = vendasNoMesVenda(s, ymKey);
    const cancelDescontos = cancelamentosNoMes(s, ymKey);
    let nTV = 0, nEnergia = 0;
    vendasVenda.forEach((v) => {
        const planos = v.planos || [];
        if (planos.some(planoContaTV)) nTV += 1;
        if (planos.some(planoContaEnergia)) nEnergia += 1;
    });
    let nTVDesc = 0, nEnergiaDesc = 0;
    cancelDescontos.forEach((v) => {
        const planos = v.planos || [];
        if (planos.some(planoContaTV)) nTVDesc += 1;
        if (planos.some(planoContaEnergia)) nEnergiaDesc += 1;
    });
    return (nTV - nTVDesc) + (nEnergia - nEnergiaDesc);
};

// Dada uma contagem, devolve o tier (escalão) correspondente da matriz TCO.
// Ex: 55 → 55. 60 → 55. 75 → 75. 9 → 0.
export const escalaoFromCount = (s, count) => {
    const escaloes = (s.tcoTable?.escaloes || [0, 10, 25, 40, 55, 75])
        .slice()
        .sort((a, b) => a - b);
    let result = escaloes[0];
    for (const e of escaloes) {
        if (count >= e) result = e;
        else break;
    }
    return result;
};

// Calcula o escalão automático para um mês (com base em count TV+Energia).
export const computeAutoEscalao = (s, ymKey) => {
    const count = countTVMaisEnergiaNoMes(s, ymKey);
    return escalaoFromCount(s, count);
};

// Insight do escalão para um mês:
// - count actual (TV+Energia)
// - escalão actual + valor por crédito
// - próximo escalão (se existir) + faltam X vendas
// - ganho extra que se obteria sobre os créditos JÁ contabilizados
//   (instalados + previstos) ao subir ao próximo tier
//
// `isAuto` indica se foi calculado automaticamente (vs manual).
// Se mês fechado, devolve null (não há nada a sugerir).
export const escalaoInsight = (s, ymKey) => {
    if (!ymKey) return null;
    const cfg = (s.mesesConfig || {})[ymKey] || {};
    if (cfg.fechado) return null;
    const escaloes = (s.tcoTable?.escaloes || [0, 10, 25, 40, 55, 75])
        .slice()
        .sort((a, b) => a - b);
    const count = countTVMaisEnergiaNoMes(s, ymKey);
    const escAct = escalaoFromCount(s, count);
    const tco = cfg.tco !== undefined ? cfg.tco : s.tcoAtual;
    const valorAct = s.tcoTable?.values?.[escAct]?.[tco] || 0;

    // Créditos totais do mês (todos, instalados+previstos) — base para o cálculo do ganho extra
    const vendasVenda = vendasNoMesVenda(s, ymKey);
    const creditosTotal = vendasVenda.reduce(
        (x, v) => x + Number(v.pacote || 0) + Number(v.pacoteEnergia || 0),
        0,
    );

    // Próximo tier acima do actual
    const idx = escaloes.indexOf(escAct);
    const proxTier = idx >= 0 && idx < escaloes.length - 1 ? escaloes[idx + 1] : null;
    let faltam = null;
    let valorProx = null;
    let ganhoExtra = null;
    if (proxTier !== null) {
        faltam = Math.max(0, proxTier - count);
        valorProx = s.tcoTable?.values?.[proxTier]?.[tco] || 0;
        ganhoExtra = (valorProx - valorAct) * creditosTotal;
    }

    return {
        count,
        escAct,
        valorAct,
        tco,
        proxTier,
        faltam,
        valorProx,
        ganhoExtra,
        creditosTotal,
        isAuto: !!s.autoEscalao,
        topo: proxTier === null,
    };
};

// Valor por crédito — opcionalmente para um mês específico
// IMPORTANTE: se o mês está FECHADO e tem snapshot `valorCredito`, devolve esse
// valor exacto — assim o mês fica imune a alterações posteriores da matriz TCO.
export const valorPorCredito = (s, monthKey) => {
    if (monthKey) {
        const cfg = (s.mesesConfig || {})[monthKey];
        if (cfg?.fechado && typeof cfg.valorCredito === "number") {
            return cfg.valorCredito;
        }
        const { escalao, tco } = getEscalaoTcoForMonth(s, monthKey);
        return s.tcoTable?.values?.[escalao]?.[tco] || 0;
    }
    return s.tcoTable?.values?.[s.escalaoAtual]?.[s.tcoAtual] || 0;
};

// Valor de uma despesa fixa para um mês específico.
// Se houver override em mesesConfig[mes].despesasFixasOverrides[fixaId], usa-o.
// Caso contrário usa o valor padrão da despesa.
// Se a despesa tem campo `desdeMes` e o mês requerido é ANTERIOR → retorna 0
// (a despesa só conta a partir do mês em que foi criada).
// IMPORTANTE: se o mês está FECHADO, retorna 0 para despesas que não existiam
// no momento do fecho (ou seja, não estão no snapshot `despesasFixasOverrides`).
export const valorDespesaFixaNoMes = (s, fixa, ymKey) => {
    if (!fixa) return 0;
    const cfg = (s.mesesConfig || {})[ymKey] || {};
    const overrides = cfg.despesasFixasOverrides || {};
    if (fixa.id in overrides) return Number(overrides[fixa.id]) || 0;
    // Mês fechado sem override desta despesa específica → esta despesa não
    // existia (ou foi adicionada depois do fecho) → não conta para este mês.
    if (cfg.fechado) return 0;
    if (fixa.desdeMes && ymKey < fixa.desdeMes) return 0;
    return Number(fixa.valor) || 0;
};

// Soma de todas as despesas fixas para um mês (com overrides aplicados).
export const totalDespesasFixasNoMes = (s, ymKey) => {
    return (s.despesasFixas || []).reduce(
        (x, d) => x + valorDespesaFixaNoMes(s, d, ymKey),
        0,
    );
};

// Mês de produção de uma venda — usa data de venda
const _monthKeyOfVenda = (v) => v?.dataVenda ? v.dataVenda.slice(0, 7) : "";

// Valor gerado por uma venda:
//   = (créditos TV/Net/Móvel + créditos Energia) × VPC do mês
//   + portabilidades × 10€
//   − valor gasto em cartões (só se plano com componente móvel)
// O VPC (escalão × TCO) aplica-se a TODOS os créditos (TV + Energia).
// Energia só é separada em CONTAGEM (para breakdown TV/Energia no painel).
// NÃO desconta comissão aqui (essa é tratada como despesa noutro KPI).
export const valorDaVenda = (v, s) => {
    const vpc = valorPorCredito(s, _monthKeyOfVenda(v));
    const base = Number(v.pacote || 0) * vpc;
    const energia = Number(v.pacoteEnergia || 0) * vpc;
    const port = Number(v.portabilidades || 0) * PORTABILIDADE_VALUE;
    const cartoes = Number(v.cartoesGasto || 0);
    return base + port + energia - cartoes;
};

// Influenciadores ativos = pessoas em despesasFixas marcadas como "influenciador"
export const getInfluenciadores = (s) =>
    (s.despesasFixas || [])
        .filter((d) => d.tipo === "influenciador")
        .map((d) => d.nome);

const activeVendas = (vendas) => (vendas || []).filter((v) => !v.cancelada);

export const totalCreditos = (vendas) =>
    activeVendas(vendas).reduce((x, v) => x + (Number(v.pacote) || 0), 0);

export const totalPortabilidades = (vendas) =>
    activeVendas(vendas).reduce((x, v) => x + (Number(v.portabilidades) || 0), 0);

export const totalAReceber = (s) =>
    activeVendas(s.vendas).reduce((x, v) => x + valorDaVenda(v, s), 0);

export const totalPago = (s) =>
    activeVendas(s.vendas).reduce((x, v) => x + valorDaVenda(v, s), 0);

export const totalDespesasFixas = (s) =>
    (s.despesasFixas || []).reduce((x, d) => x + (Number(d.valor) || 0), 0);

export const totalDespesasExtras = (s) =>
    (s.despesasExtras || []).reduce((x, d) => x + (Number(d.valor) || 0), 0);

export const totalComissoes = (s) =>
    activeVendas(s.vendas).reduce((x, v) => x + (Number(v.comissao) || 0), 0);

export const totalDespesas = (s) =>
    totalDespesasFixas(s) + totalDespesasExtras(s) + totalComissoes(s);

export const lucroFinal = (s) => totalPago(s) - totalDespesas(s);

export const creditosInstalados = (vendas) =>
    activeVendas(vendas)
        .filter((v) => v.instalado)
        .reduce((x, v) => x + (Number(v.pacote) || 0), 0);

export const percentMeta = (s) =>
    Math.min(
        100,
        Math.round((creditosInstalados(s.vendas) / (s.metaCreditos || 1)) * 100),
    );

export const vendasPorZona = (vendas) => {
    const m = {};
    activeVendas(vendas).forEach((v) => {
        const k = v.zona || "—";
        m[k] = (m[k] || 0) + Number(v.pacote || 0);
    });
    return Object.entries(m)
        .map(([zona, creditos]) => ({ zona, creditos }))
        .sort((a, b) => b.creditos - a.creditos);
};

export const vendasPorVPP = (vendas) => {
    const m = {};
    activeVendas(vendas).forEach((v) => {
        const k = v.vppNome || "—";
        if (!m[k]) m[k] = { vpp: k, vendas: 0, creditos: 0, valorPago: 0 };
        m[k].vendas += 1;
        m[k].creditos += Number(v.pacote || 0);
    });
    return Object.values(m).sort((a, b) => b.creditos - a.creditos);
};

export const vendasPorPlano = (vendas) => {
    const m = {};
    activeVendas(vendas).forEach((v) => {
        (v.planos || []).forEach((p) => {
            m[p] = (m[p] || 0) + 1;
        });
    });
    return PLANOS.map((p) => ({ plano: p, count: m[p] || 0 }));
};

const MESES = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

export const vendasPorMes = (vendas) => {
    const m = {};
    activeVendas(vendas).forEach((v) => {
        const d = new Date(v.dataVenda);
        const k = MESES[d.getMonth()];
        m[k] = (m[k] || 0) + Number(v.pacote || 0);
    });
    const order = [];
    for (let i = 5; i >= 0; i--) {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        order.push(MESES[d.getMonth()]);
    }
    return order.map((mes) => ({ mes, creditos: m[mes] || 0 }));
};

/**
 * Ranking de influenciadores:
 * - usa apenas despesas fixas com tipo === "influenciador"
 * - soma créditos e receita das vendas onde fonteTipo=Influenciadores e fonteNome=<influ>
 * - lucro = receita − custo mensal
 */
export const influenciadoresRanking = (s, ymKey) => {
    const influs = (s.despesasFixas || []).filter(
        (d) => d.tipo === "influenciador",
    );
    // Se houver mês, filtra vendas pelo mês de registo (dataVenda)
    const vendasBase = ymKey
        ? activeVendas(s.vendas).filter((v) => (v.dataVenda || "").startsWith(ymKey))
        : activeVendas(s.vendas);
    return influs
        .map((infl) => {
            const vendas = vendasBase.filter(
                (v) =>
                    v.fonteTipo === "Influenciadores" &&
                    v.fonteNome === infl.nome,
            );
            const creditos = vendas.reduce((x, v) => x + Number(v.pacote || 0), 0);
            const portab = vendas.reduce((x, v) => x + Number(v.portabilidades || 0), 0);
            const receita = vendas.reduce((x, v) => x + valorDaVenda(v, s), 0);
            // Usa override do mês se fornecido, senão valor padrão
            const custo = ymKey
                ? valorDespesaFixaNoMes(s, infl, ymKey)
                : Number(infl.valor || 0);
            const lucro = receita - custo;
            return {
                nome: infl.nome,
                custoMensal: custo,
                vendas: vendas.length,
                creditos,
                portabilidades: portab,
                receita,
                lucro,
            };
        })
        .sort((a, b) => b.lucro - a.lucro);
};

/**
 * Ranking de fontes externas (NÃO influenciadores):
 * Relocation, Instagram, Indicação, Outros
 */
export const fontesExternasRanking = (s, ymKey) => {
    const m = {};
    const vendasBase = ymKey
        ? activeVendas(s.vendas).filter((v) => (v.dataVenda || "").startsWith(ymKey))
        : activeVendas(s.vendas);
    vendasBase.forEach((v) => {
        if (v.fonteTipo === "Influenciadores") return;
        let label;
        if (v.fonteTipo === "Instagram") label = "Instagram";
        else if (v.fonteTipo === "Lead Influencer")
            label = `Lead Influencer · ${v.fonteNome || "s/ nome"}`;
        else if (v.fonteTipo === "Relocation")
            label = `Relocation · ${v.fonteNome || "s/ nome"}`;
        else if (v.fonteTipo === "Indicação")
            label = `Indicação · ${v.fonteNome || "s/ nome"}`;
        else label = `Outros · ${v.fonteNome || "s/ nome"}`;

        const b = m[label] || {
            fonte: label,
            tipo: v.fonteTipo,
            vendas: 0,
            creditos: 0,
            receita: 0,
            comissao: 0,
        };
        b.vendas += 1;
        b.creditos += Number(v.pacote || 0);
        b.receita += valorDaVenda(v, s);
        b.comissao += Number(v.comissao || 0);
        m[label] = b;
    });
    return Object.values(m)
        .map((b) => ({ ...b, lucro: b.receita - b.comissao }))
        .sort((a, b) => b.creditos - a.creditos);
};

export const formatEUR = (n) =>
    new Intl.NumberFormat("pt-PT", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
    }).format(n || 0);

export const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("pt-PT", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

export const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// ============ PAYMENT / PRODUÇÃO CALC ============
// Regra (confirmada pelo Salvador, 13/05/2026):
//  - Mês de produção = SEMPRE o mês em que a venda foi REGISTADA (dataVenda).
//  - Mês de recebimento = fim do mês onde cai o "fecho" (dia 8) que cobre a
//    data de instalação. O 1º fecho candidato é dia 8 do mês seguinte ao da
//    venda; se a instalação for posterior, salta para o fecho do mês seguinte.
//  Exemplos (venda em Maio):
//    Instalada 01/05 → 08/06 → Recebe fim de Junho
//    Instalada 09/06 → 08/07 → Recebe fim de Julho
//    Instalada 09/07 → 08/08 → Recebe fim de Agosto

export const producaoMes = (venda) => {
    // Só conta para produção se a venda estiver MARCADA como instalada.
    // A dataInstalacao sozinha é apenas previsão e não conta para recebimentos.
    if (!venda?.instalado) return null;
    if (!venda?.dataVenda) return null;
    const d = new Date(venda.dataVenda);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), 1);
};

// Mês de produção baseado na DATA DE INSTALAÇÃO + DATA DE VENDA (regra híbrida).
//
// Regras:
//   1) Se a venda foi registada no MESMO mês da instalação (ou depois): produção = mês da venda.
//      Ex: venda Junho + instala 05/06 → Junho.
//   2) Se a instalação foi num mês POSTERIOR ao da venda: aplica regra do dia 8.
//      - Instalação dia 1-8 → produção = mês anterior à instalação.
//      - Instalação dia 9+ → produção = mês da instalação.
//      Mas nunca pode descer abaixo do mês em que a venda foi registada.
//
// Usado na página /instalacoes para agrupar e no banner Produção do Dashboard.
export const producaoMesKeyByInstalacao = (venda) => {
    if (!venda?.dataInstalacao) return null;
    const inst = new Date(venda.dataInstalacao);
    if (Number.isNaN(inst.getTime())) return null;
    const instKey = `${inst.getFullYear()}-${String(inst.getMonth() + 1).padStart(2, "0")}`;

    // Se não há dataVenda, usa apenas a regra do dia 8 sem floor
    if (!venda.dataVenda) {
        const prod = new Date(inst.getFullYear(), inst.getMonth(), 1);
        if (inst.getDate() <= 8) prod.setMonth(prod.getMonth() - 1);
        return `${prod.getFullYear()}-${String(prod.getMonth() + 1).padStart(2, "0")}`;
    }
    const vd = new Date(venda.dataVenda);
    if (Number.isNaN(vd.getTime())) return instKey;
    const vendaKey = `${vd.getFullYear()}-${String(vd.getMonth() + 1).padStart(2, "0")}`;

    // Caso 1: instalou no mesmo mês ou antes da venda → produção = mês da venda
    if (instKey <= vendaKey) return vendaKey;

    // Caso 2: instalou em mês posterior → regra dia 8
    const prod = new Date(inst.getFullYear(), inst.getMonth(), 1);
    if (inst.getDate() <= 8) prod.setMonth(prod.getMonth() - 1);
    const prodKey = `${prod.getFullYear()}-${String(prod.getMonth() + 1).padStart(2, "0")}`;

    // Floor: nunca abaixo do mês de venda
    return prodKey < vendaKey ? vendaKey : prodKey;
};

export const dataRecebimento = (venda) => {
    if (!venda?.instalado) return null;
    if (!venda?.dataVenda || !venda?.dataInstalacao) return null;
    const venda_d = new Date(venda.dataVenda);
    const inst_d = new Date(venda.dataInstalacao);
    if (Number.isNaN(venda_d.getTime()) || Number.isNaN(inst_d.getTime())) return null;
    // Primeiro ciclo de fecho: dia 8 23:59 do mês seguinte ao da venda
    let cycleEnd = new Date(venda_d.getFullYear(), venda_d.getMonth() + 1, 8, 23, 59, 59);
    let safety = 0;
    while (inst_d > cycleEnd && safety < 24) {
        cycleEnd = new Date(cycleEnd.getFullYear(), cycleEnd.getMonth() + 1, 8, 23, 59, 59);
        safety += 1;
    }
    // Recebimento = último dia do mês do cycleEnd
    return new Date(cycleEnd.getFullYear(), cycleEnd.getMonth() + 1, 0);
};

const MESES_LONG = [
    "Janeiro","Fevereiro","Março","Abril","Maio","Junho",
    "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro",
];

export const formatMesAno = (d) => {
    if (!d) return "—";
    return `${MESES_LONG[d.getMonth()]} ${d.getFullYear()}`;
};

// Agrupa vendas por mês de recebimento (YYYY-MM)
export const recebimentosPorMes = (s) => {
    const m = {};
    activeVendas(s.vendas).forEach((v) => {
        const rec = dataRecebimento(v);
        if (!rec) {
            // sem instalação confirmada → aguarda confirmação manual
            const k = "pending";
            if (!m[k]) m[k] = { key: k, label: "Aguarda confirmação de instalação", date: null, vendas: [], total: 0, creditos: 0 };
            m[k].vendas.push(v);
            m[k].total += valorDaVenda(v, s);
            m[k].creditos += Number(v.pacote || 0);
            return;
        }
        const k = `${rec.getFullYear()}-${String(rec.getMonth() + 1).padStart(2, "0")}`;
        if (!m[k])
            m[k] = {
                key: k,
                label: `Fim de ${formatMesAno(rec)}`,
                date: rec,
                vendas: [],
                total: 0,
                creditos: 0,
            };
        m[k].vendas.push(v);
        m[k].total += valorDaVenda(v, s);
        m[k].creditos += Number(v.pacote || 0);
    });
    // sort: pending last, others by date asc
    return Object.values(m).sort((a, b) => {
        if (a.key === "pending") return 1;
        if (b.key === "pending") return -1;
        return a.date - b.date;
    });
};

// ============ MONTH FILTERING ============

const monthKey = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

export const currentMonthKey = () => monthKey(new Date());

export const formatMonthKey = (k) => {
    if (!k) return "";
    const [y, m] = k.split("-").map(Number);
    return formatMesAno(new Date(y, m - 1, 1));
};

// Formata créditos: máx 2 casas decimais, sem zeros à direita.
// Ex: 60.999999998 → "61"; 60.50 → "60,5"; 60.123 → "60,12"
export const formatCreditos = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return "0";
    const rounded = Math.round(v * 100) / 100;
    // ToString com locale PT (vírgula decimal), removendo .00
    return rounded.toLocaleString("pt-PT", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
};

// Lista de meses com vendas registadas + 6 meses futuros + atual
export const availableMonths = (s) => {
    const set = new Set();
    activeVendas(s.vendas).forEach((v) => {
        const prod = producaoMes(v);
        if (prod) set.add(monthKey(prod));
        if (v.dataVenda) {
            const d = new Date(v.dataVenda);
            set.add(monthKey(d));
        }
    });
    // garantir mes atual e 6 meses passados/futuros
    const today = new Date();
    for (let i = -6; i <= 6; i++) {
        const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
        set.add(monthKey(d));
    }
    return Array.from(set).sort().reverse();
};

// Filtra vendas pelo mês de PRODUÇÃO (mais relevante para receita)
export const vendasNoMesProducao = (s, ymKey) => {
    return activeVendas(s.vendas).filter((v) => {
        const prod = producaoMes(v);
        if (!prod) return false;
        return monthKey(prod) === ymKey;
    });
};

// Filtra vendas pelo mês de VENDA (data da venda).
// Inclui vendas canceladas no MESMO mês — a subtração acontece via
// `cancelamentosNoMes` para dar transparência ao utilizador (+1 na contagem
// bruta, -1 na lista de descontos, líquido zero).
export const vendasNoMesVenda = (s, ymKey) => {
    return (s.vendas || []).filter((v) => {
        if (!v.dataVenda) return false;
        if (monthKey(new Date(v.dataVenda)) !== ymKey) return false;
        // Legacy: cancelada sem canceladaEm — não temos data para descontar,
        // por segurança excluímos (evita contabilizar sem forma de descontar).
        if (v.cancelada && !v.canceladaEm) return false;
        return true;
    });
};

// Cancelamentos NEGATIVOS para o mês X.
// Inclui QUALQUER venda cancelada em X (independentemente do mês de venda,
// contanto que o cancelamento não seja anterior à venda).
// Para vendas + cancelamento no MESMO mês: +1 em vendasNoMesVenda e -1 aqui = 0 (mas visível).
// Para venda em mês anterior + cancelamento em X: só o -1 é aplicado (venda contou no seu mês).
export const cancelamentosNoMes = (s, ymKey) => {
    return (s.vendas || []).filter((v) => {
        if (!v.cancelada || !v.canceladaEm) return false;
        if (!v.dataVenda) return false;
        const mc = monthKey(new Date(v.canceladaEm));
        const mv = monthKey(new Date(v.dataVenda));
        // Cancelamento não pode ser anterior à venda (dados corrompidos → skip)
        if (mv > mc) return false;
        return mc === ymKey;
    });
};

export const despesasExtrasNoMes = (s, ymKey) => {
    return (s.despesasExtras || []).filter(
        (d) => d.data && d.data.startsWith(ymKey),
    );
};

// Vendas PREVISTAS para um mês de produção (data prevista mas ainda não confirmadas).
// Aplica a mesma regra híbrida que producaoMesKeyByInstalacao.
export const vendasPrevistasNoMesProducao = (s, ymKey) => {
    return activeVendas(s.vendas).filter((v) => {
        if (v.instalado) return false; // já confirmadas usam o caminho normal
        if (!v.dataInstalacao) return false;
        return producaoMesKeyByInstalacao(v) === ymKey;
    });
};

// Vendas INSTALADAS cujo mês de produção (regra híbrida) cai no mês X.
// Esta é a fonte de verdade para o banner "Produção {mês}" no dashboard.
export const vendasInstaladasComProducaoNoMes = (s, ymKey) => {
    return activeVendas(s.vendas).filter((v) => {
        if (!v.instalado) return false;
        if (!v.dataInstalacao) return false;
        return producaoMesKeyByInstalacao(v) === ymKey;
    });
};

// KPIs do mês selecionado
export const kpisDoMes = (s, ymKey) => {
    const vendasProd = vendasNoMesProducao(s, ymKey);
    const vendasVenda = vendasNoMesVenda(s, ymKey);
    const vendasPrev = vendasPrevistasNoMesProducao(s, ymKey);
    // Cancelamentos vindos de meses anteriores que descontam neste mês
    const cancelDescontos = cancelamentosNoMes(s, ymKey);
    const extras = despesasExtrasNoMes(s, ymKey);

    const receita = vendasProd.reduce((x, v) => x + valorDaVenda(v, s), 0);
    const creditos = vendasProd.reduce((x, v) => x + Number(v.pacote || 0), 0);
    const portab = vendasProd.reduce((x, v) => x + Number(v.portabilidades || 0), 0);
    const comissoes = vendasProd.reduce((x, v) => x + Number(v.comissao || 0), 0);

    // Breakdown TV / Energia e contagem por plano
    // Para Família/Zona/Produzido usa-se TODAS as vendas registadas no mês (dataVenda),
    // não só as instaladas — porque queremos ver o esforço comercial completo.
    let creditosTV = 0, creditosEnergia = 0;
    let nTV = 0, nEnergia = 0;
    // Soma INDIVIDUAL de planos (cada plano TV/Energia conta uma vez, mesmo dentro da mesma venda).
    // Ex: 10 vendas de M4 + 2 de M3 + 1 de "M2 TV+Net 2ª Hab" → nPlanosTV = 13
    let nPlanosTV = 0, nPlanosEnergia = 0;
    const porPlano = {};
    const porZona = {};
    const porFamilia = { M1: 0, M2: 0, M3: 0, M4: 0, Energia: 0, Móvel: 0 };
    let creditosProduzidos = 0;
    // Planos relevantes para o painel "Por zona" (TV + Energia)
    // Mapeamento: nome interno → label curto para UI
    const ZONA_PLANOS = [
        { key: "M1 TV", label: "M1 TV" },
        { key: "M2 TV+Net 2ª Hab", label: "M2 (2ªH)" },
        { key: "M3", label: "M3" },
        { key: "M4", label: "M4" },
        { key: "Energia", label: "Energia" },
    ];
    const emptyPlanos = () => Object.fromEntries(
        ZONA_PLANOS.map((p) => [p.key, { ate15: 0, total: 0 }]),
    );
    vendasVenda.forEach((v) => {
        const c = Number(v.pacote || 0);
        const cEn = Number(v.pacoteEnergia || 0);
        creditosProduzidos += c + cEn;
        const planos = v.planos || [];
        const temTV = planos.some(planoContaTV);
        const temEn = planos.some(planoContaEnergia);
        if (temTV) { creditosTV += c; nTV += 1; }
        if (temEn) {
            // Energia usa o seu PRÓPRIO campo de créditos, independente do pacote.
            creditosEnergia += cEn;
            nEnergia += 1;
        }
        // Conta cada plano TV/Energia individualmente
        nPlanosTV += planos.filter(planoContaTV).length;
        nPlanosEnergia += planos.filter(planoContaEnergia).length;
        planos.forEach((p) => {
            porPlano[p] = (porPlano[p] || 0) + 1;
            // Família M1/M2/M3/M4 (apanha "M1 TV", "M2 Net+Móvel", etc)
            const m = p.match(/^M(\d)/);
            if (m) {
                const fam = `M${m[1]}`;
                if (fam in porFamilia) porFamilia[fam] += 1;
            } else if (p === "Energia") porFamilia.Energia += 1;
            else if (p === "Móvel") porFamilia.Móvel += 1;
        });
        const z = (v.zona || "").trim() || "Sem zona";
        if (!porZona[z]) porZona[z] = { vendas: 0, creditos: 0, planos: emptyPlanos() };
        porZona[z].vendas += 1;
        porZona[z].creditos += c + cEn;
        // Breakdown por plano + cutoff dia 15 (Europe/Lisbon usa dataVenda como string YYYY-MM-DD)
        const dia = parseInt(((v.dataVenda || "").slice(8, 10) || "0"), 10) || 0;
        const ate15 = dia > 0 && dia <= 15;
        ZONA_PLANOS.forEach(({ key }) => {
            if (planos.includes(key)) {
                porZona[z].planos[key].total += 1;
                if (ate15) porZona[z].planos[key].ate15 += 1;
            }
        });
    });
    // Aplica DESCONTOS dos cancelamentos provenientes de meses anteriores
    let nTVDesconto = 0, nEnergiaDesconto = 0;
    let nPlanosTVDesconto = 0, nPlanosEnergiaDesconto = 0;
    let creditosTVDesconto = 0, creditosEnergiaDesconto = 0;
    cancelDescontos.forEach((v) => {
        const planos = v.planos || [];
        const c = Number(v.pacote || 0);
        const cEn = Number(v.pacoteEnergia || 0);
        if (planos.some(planoContaTV)) { nTVDesconto += 1; creditosTVDesconto += c; }
        if (planos.some(planoContaEnergia)) { nEnergiaDesconto += 1; creditosEnergiaDesconto += cEn; }
        nPlanosTVDesconto += planos.filter(planoContaTV).length;
        nPlanosEnergiaDesconto += planos.filter(planoContaEnergia).length;
        // Também desconta em porZona / porPlano / porFamilia / creditosProduzidos
        // (senão a tabela "Por Zona" mostra o total inflado por vendas já anuladas)
        creditosProduzidos -= (c + cEn);
        planos.forEach((p) => {
            if (porPlano[p]) porPlano[p] = Math.max(0, porPlano[p] - 1);
            const m = p.match(/^M(\d)/);
            if (m) {
                const fam = `M${m[1]}`;
                if (fam in porFamilia) porFamilia[fam] = Math.max(0, porFamilia[fam] - 1);
            } else if (p === "Energia") porFamilia.Energia = Math.max(0, porFamilia.Energia - 1);
            else if (p === "Móvel") porFamilia.Móvel = Math.max(0, porFamilia.Móvel - 1);
        });
        const z = (v.zona || "").trim() || "Sem zona";
        if (porZona[z]) {
            porZona[z].vendas = Math.max(0, porZona[z].vendas - 1);
            porZona[z].creditos = Math.max(0, porZona[z].creditos - (c + cEn));
            const diaCancelOrig = parseInt(((v.dataVenda || "").slice(8, 10) || "0"), 10) || 0;
            const ate15Orig = diaCancelOrig > 0 && diaCancelOrig <= 15;
            ZONA_PLANOS.forEach(({ key }) => {
                if (planos.includes(key) && porZona[z].planos[key]) {
                    porZona[z].planos[key].total = Math.max(0, porZona[z].planos[key].total - 1);
                    if (ate15Orig) {
                        porZona[z].planos[key].ate15 = Math.max(0, porZona[z].planos[key].ate15 - 1);
                    }
                }
            });
        }
    });
    // Ajustados = positivos - descontos (podem ficar negativos se cancelamentos > registos do mês)
    const nTVAjustado = nTV - nTVDesconto;
    const nEnergiaAjustado = nEnergia - nEnergiaDesconto;
    const nPlanosTVAjustado = nPlanosTV - nPlanosTVDesconto;
    const nPlanosEnergiaAjustado = nPlanosEnergia - nPlanosEnergiaDesconto;
    const creditosTVAjustado = creditosTV - creditosTVDesconto;
    const creditosEnergiaAjustado = creditosEnergia - creditosEnergiaDesconto;
    // Créditos instalados = subset das vendas registadas no mês que já estão instaladas
    const creditosInstalados = vendasVenda
        .filter((v) => !!v.instalado)
        .reduce((x, v) => x + Number(v.pacote || 0), 0);
    const totalExtras = extras.reduce((x, d) => x + Number(d.valor || 0), 0);
    const fixas = totalDespesasFixasNoMes(s, ymKey);
    const despesasMes = fixas + totalExtras + comissoes;
    const lucro = receita - despesasMes;

    // Previstos
    const previstoReceita = vendasPrev.reduce((x, v) => x + valorDaVenda(v, s), 0);
    const previstoCreditos = vendasPrev.reduce((x, v) => x + Number(v.pacote || 0), 0);

    // Métricas Digital vs Papel — só para vendas TV (M1 TV, M2 TV+Net 2ª Hab, M3, M4)
    // contam apenas as que JÁ TÊM contrato carregado.
    const vendasTV = vendasVenda.filter((v) =>
        (v.planos || []).some(planoContaTV),
    );
    const vendasTVComContrato = vendasTV.filter((v) => !!v.contratoCarregado);
    const vendasTVDigitais = vendasTVComContrato.filter((v) => v.contratoDigital === true);
    const vendasTVPapel = vendasTVComContrato.filter((v) => v.contratoDigital === false);
    const pctDigital = vendasTVComContrato.length > 0
        ? (vendasTVDigitais.length / vendasTVComContrato.length) * 100
        : 0;
    // Breakdown por VPP (responsável pelo registo da venda)
    // Agrupa por vppNome e devolve { totais, digitais, papel, pct } para cada um.
    const digitalPorVpp = {};
    vendasTVComContrato.forEach((v) => {
        const k = (v.vppNome || "").trim() || "—";
        if (!digitalPorVpp[k]) {
            digitalPorVpp[k] = { vpp: k, totais: 0, digitais: 0, papel: 0, semFlag: 0 };
        }
        const b = digitalPorVpp[k];
        b.totais += 1;
        if (v.contratoDigital === true) b.digitais += 1;
        else if (v.contratoDigital === false) b.papel += 1;
        else b.semFlag += 1;
    });
    Object.values(digitalPorVpp).forEach((b) => {
        b.pct = b.totais > 0 ? (b.digitais / b.totais) * 100 : 0;
    });

    // Margem (% de lucro sobre receita)
    const margem = receita > 0 ? (lucro / receita) * 100 : 0;

    // ─────────── Ritmo do mês (projecção linear) ───────────
    // Compara o ritmo actual com o que falta no mês para projectar o total
    // final. Útil para saber se vais atingir a meta ou não.
    const [yStr, mStr] = ymKey.split("-");
    const yNum = Number(yStr); const mNum = Number(mStr);
    const isMesAtual = (() => {
        const now = new Date();
        return now.getFullYear() === yNum && (now.getMonth() + 1) === mNum;
    })();
    let ritmo = null;
    if (isMesAtual && yNum && mNum) {
        const now = new Date();
        const diasNoMes = new Date(yNum, mNum, 0).getDate();
        const diaActual = Math.max(1, now.getDate());
        const diasRestantes = Math.max(0, diasNoMes - diaActual);
        // Ritmo dos últimos 7 dias (vendas + créditos)
        const seteDiasAtras = new Date(now); seteDiasAtras.setDate(seteDiasAtras.getDate() - 7);
        const seteDiasStr = seteDiasAtras.toISOString().slice(0, 10);
        const vendasUltimos7 = vendasVenda.filter((v) => (v.dataVenda || "") >= seteDiasStr);
        const credUltimos7 = vendasUltimos7.reduce((x, v) => x + Number(v.pacote || 0) + Number(v.pacoteEnergia || 0), 0);
        const recUltimos7 = vendasUltimos7.reduce((x, v) => x + valorDaVenda(v, s), 0);
        const credPorDia = credUltimos7 / 7;
        const recPorDia = recUltimos7 / 7;
        const projeccaoCreditos = creditos + (credPorDia * diasRestantes);
        const projeccaoReceita = receita + (recPorDia * diasRestantes);
        const meta = getMetaForMonth(s, ymKey) || 3000;
        const pctMeta = meta > 0 ? (creditos / meta) * 100 : 0;
        const pctProjMeta = meta > 0 ? (projeccaoCreditos / meta) * 100 : 0;
        ritmo = {
            diaActual,
            diasNoMes,
            diasRestantes,
            credPorDia: Math.round(credPorDia * 10) / 10,
            recPorDia: Math.round(recPorDia),
            projeccaoCreditos: Math.round(projeccaoCreditos),
            projeccaoReceita: Math.round(projeccaoReceita),
            pctMeta: Math.round(pctMeta),
            pctProjMeta: Math.round(pctProjMeta),
            vaiDar: projeccaoCreditos >= meta,
            meta,
        };
    }

    return {
        receita,
        creditos,
        portabilidades: portab,
        comissoes,
        despesasFixas: fixas,
        despesasExtras: totalExtras,
        despesasTotal: despesasMes,
        lucro,
        margem,
        ritmo,
        // Contrato digital vs papel (só TV)
        nTVComContrato: vendasTVComContrato.length,
        nTVDigitais: vendasTVDigitais.length,
        nTVPapel: vendasTVPapel.length,
        pctDigital,
        digitalPorVpp: Object.values(digitalPorVpp).sort((a, b) => b.totais - a.totais),
        vendasProducao: vendasProd,
        vendasRegistadas: vendasVenda,
        nProducao: vendasProd.length,
        nVenda: vendasVenda.length,
        // Breakdown por tipo
        creditosTV,
        creditosEnergia,
        creditosTVAjustado,
        creditosEnergiaAjustado,
        nTV,
        nEnergia,
        nPlanosTV,
        nPlanosEnergia,
        // Ajustados (positivos - cancelamentos vindos de outros meses, mínimo 0)
        nTVAjustado,
        nEnergiaAjustado,
        nPlanosTVAjustado,
        nPlanosEnergiaAjustado,
        // Vendas que descontam neste mês (originárias de mês anterior, anuladas aqui)
        cancelamentosDescontados: cancelDescontos,
        porPlano,
        porZona,
        porFamilia,
        creditosProduzidos,
        creditosInstalados,
        creditosPendentes: creditosProduzidos - creditosInstalados,
        // Previstos (com dataInstalacao mas instalado=false)
        previstoReceita,
        previstoCreditos,
        previstoVendas: vendasPrev,
        nPrevisto: vendasPrev.length,
        // Total potencial = confirmado + previsto
        totalPotencialReceita: receita + previstoReceita,
        totalPotencialCreditos: creditos + previstoCreditos,
    };
};

// CSV Export (Excel-friendly with UTF-8 BOM + ;)
export const vendasParaCSV = (s, vendas) => {
    const sep = ";";
    const headers = [
        "ID",
        "Data Venda",
        "Cliente",
        "NIF",
        "Zona",
        "VPP",
        "Créditos",
        "Planos",
        "Portabilidades",
        "Data Instalação",
        "Mês Produção",
        "Data Recebimento",
        "Fonte Tipo",
        "Fonte Nome",
        "Comissão (€)",
        "Valor (€)",
        "Estado",
    ];
    const rows = vendas.map((v) => {
        const prod = producaoMes(v);
        const rec = dataRecebimento(v);
        return [
            v.id,
            v.dataVenda || "",
            v.nome || "",
            v.nif || "",
            v.zona || "",
            v.vppNome || "",
            v.pacote || 0,
            (v.planos || []).join("|"),
            v.portabilidades || 0,
            v.dataInstalacao || "",
            prod ? formatMesAno(prod) : "",
            rec ? rec.toISOString().slice(0, 10) : "",
            v.fonteTipo || "",
            v.fonteNome || "",
            (v.comissao || 0).toString().replace(".", ","),
            valorDaVenda(v, s).toFixed(2).replace(".", ","),
            v.cancelada ? "Anulada" : v.instalado ? "Instalado" : "Pendente",
        ]
            .map((c) => {
                const str = String(c);
                if (str.includes(sep) || str.includes('"') || str.includes("\n")) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
            })
            .join(sep);
    });
    const BOM = "\uFEFF";
    return BOM + headers.join(sep) + "\n" + rows.join("\n");
};

export const downloadCSV = (filename, csv) => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// Próximo recebimento (grupo futuro mais próximo) — visão GLOBAL
export const proximoRecebimento = (s) => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const grupos = recebimentosPorMes(s).filter((g) => g.date && g.date >= hoje);
    return grupos[0] || null;
};

// Recebimento associado à PRODUÇÃO de um mês específico.
// Ex: vendas instaladas em Maio recebem em fim de Junho → quando mesSel="2026-05",
// devolve o grupo com key="2026-06".
// Útil para o painel mostrar "o que este mês me vai render", separado do mês actual.
export const recebimentoDoMesProducao = (s, ymKey) => {
    if (!ymKey) return null;
    const [y, m] = ymKey.split("-").map(Number);
    if (!y || !m) return null;
    // Mês de recebimento = mês seguinte
    const next = new Date(y, m, 1); // m porque mes 1-indexed e Date é 0-indexed → m vira mês seguinte
    const recKey = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`;
    const grupos = recebimentosPorMes(s);
    return grupos.find((g) => g.key === recKey) || null;
};

// ============ INCENTIVOS ============

// Ranking dos incentivos: quantas vendas usaram cada cupão + quem criou (fonteNome)
export const incentivosRanking = (s) => {
    const m = {};
    activeVendas(s.vendas).forEach((v) => {
        const inc = (v.incentivo || "").trim();
        if (!inc) return;
        if (!m[inc])
            m[inc] = {
                incentivo: inc,
                vendas: 0,
                creditos: 0,
                receita: 0,
                criadores: new Set(),
            };
        m[inc].vendas += 1;
        m[inc].creditos += Number(v.pacote || 0);
        m[inc].receita += valorDaVenda(v, s);
        // Quem criou = quem indicou (fonteNome se influenciador)
        if (v.fonteTipo === "Influenciadores" && v.fonteNome) {
            m[inc].criadores.add(v.fonteNome);
        }
    });
    return Object.values(m)
        .map((b) => ({ ...b, criadores: Array.from(b.criadores) }))
        .sort((a, b) => b.vendas - a.vendas);
};

// Ranking de quem CRIOU mais cupões (influenciadores)
export const criadoresIncentivosRanking = (s) => {
    const m = {};
    activeVendas(s.vendas).forEach((v) => {
        const inc = (v.incentivo || "").trim();
        if (!inc) return;
        if (v.fonteTipo !== "Influenciadores" || !v.fonteNome) return;
        const k = v.fonteNome;
        if (!m[k]) m[k] = { criador: k, vendas: 0, codigos: new Set() };
        m[k].vendas += 1;
        m[k].codigos.add(inc);
    });
    return Object.values(m)
        .map((b) => ({
            ...b,
            codigos: Array.from(b.codigos),
            nCodigos: b.codigos.size,
        }))
        .sort((a, b) => b.vendas - a.vendas);
};

// ============ ALERTAS ============

const daysBetween = (a, b) => Math.floor((b - a) / 86400000);

const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};

const addMonths = (date, months) => {
    const d = new Date(date);
    d.setMonth(d.getMonth() + months);
    return d;
};

/**
 * Gera alertas pendentes baseado nas datas e estado:
 * - "vender-energia": 15 dias após venda + sem plano Energia
 * - "follow-2m": 2 meses após instalação
 * - "follow-4m": 4 meses após instalação
 */
export const alertasPendentes = (s) => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    // suporta tanto array de strings (legado) como array de objetos {id, nota}
    const dispensadosIds = new Set(
        (s.alertasDispensados || []).map((d) => (typeof d === "string" ? d : d.id)),
    );
    const out = [];

    activeVendas(s.vendas).forEach((v) => {
        // 0) Energia "por tratar" (Nova venda 2026-10) — alerta local até mudar de estado; não dispensável
        if (["por_tratar", "senhorio", "em_acompanhamento", "nao_perguntei"].includes(v.energiaEstado) && (v.energiaProximoContacto || v.energiaEstado === "por_tratar")) {
            const trigger = v.energiaProximoContacto ? new Date(v.energiaProximoContacto) : new Date(v.dataVenda || hoje);
            trigger.setHours(0, 0, 0, 0);
            if (trigger <= hoje) {
                out.push({
                    id: `${v.id}::energia-por-tratar`,
                    tipo: "energia-por-tratar",
                    titulo: "Energia por tratar",
                    desc: `${v.nome} — contacto previsto ${v.energiaProximoContacto || "—"}${v.energiaObs ? ` · ${v.energiaObs}` : ""}. Resolve na ficha da venda (Feita / Não quis / Não aplicável).`,
                    venda: v,
                    triggerDate: trigger,
                    cor: "amber",
                    naoDispensavel: true,
                });
            }
        }
        // 1) 15 dias após venda + sem Energia (não dispara quando a energia já tem estado decidido)
        if (v.dataVenda && !(v.planos || []).includes("Energia") && !v.energiaEstado) {
            const trigger = addDays(new Date(v.dataVenda), 15);
            const id = `${v.id}::vender-energia`;
            if (trigger <= hoje && !dispensadosIds.has(id)) {
                out.push({
                    id,
                    tipo: "vender-energia",
                    titulo: "Vender Energia",
                    desc: `${v.nome} fez ${daysBetween(new Date(v.dataVenda), hoje)} dias sem aderir à Energia.`,
                    venda: v,
                    triggerDate: trigger,
                    cor: "amber",
                });
            }
        }

        // 2) 2 meses após instalação
        if (v.instalado && v.dataInstalacao) {
            const trigger = addMonths(new Date(v.dataInstalacao), 2);
            const id = `${v.id}::follow-2m`;
            if (trigger <= hoje && !dispensadosIds.has(id)) {
                out.push({
                    id,
                    tipo: "follow-2m",
                    titulo: "Follow-up 2 meses",
                    desc: `${v.nome} — confirmar se as faturas estão corretas e tudo a correr bem.`,
                    venda: v,
                    triggerDate: trigger,
                    cor: "blue",
                });
            }
        }

        // 3) 4 meses após instalação
        if (v.instalado && v.dataInstalacao) {
            const trigger = addMonths(new Date(v.dataInstalacao), 4);
            const id = `${v.id}::follow-4m`;
            if (trigger <= hoje && !dispensadosIds.has(id)) {
                out.push({
                    id,
                    tipo: "follow-4m",
                    titulo: "Follow-up 4 meses",
                    desc: `${v.nome} — acompanhamento periódico.`,
                    venda: v,
                    triggerDate: trigger,
                    cor: "purple",
                });
            }
        }
    });

    return out.sort((a, b) => a.triggerDate - b.triggerDate);
};

// Próximos alertas (ainda não atingidos) — para visão preditiva
export const alertasProximos = (s) => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const limite = addDays(hoje, 30);
    const dispensadosIds = new Set(
        (s.alertasDispensados || []).map((d) => (typeof d === "string" ? d : d.id)),
    );
    const out = [];

    activeVendas(s.vendas).forEach((v) => {
        if (["por_tratar", "senhorio", "em_acompanhamento", "nao_perguntei"].includes(v.energiaEstado) && v.energiaProximoContacto) {
            const trigger = new Date(v.energiaProximoContacto);
            trigger.setHours(0, 0, 0, 0);
            if (trigger > hoje && trigger <= limite) {
                out.push({ id: `${v.id}::energia-por-tratar`, tipo: "energia-por-tratar", titulo: "Energia por tratar", venda: v, triggerDate: trigger, cor: "amber" });
            }
        }
        if (v.dataVenda && !(v.planos || []).includes("Energia") && !v.energiaEstado) {
            const trigger = addDays(new Date(v.dataVenda), 15);
            const id = `${v.id}::vender-energia`;
            if (trigger > hoje && trigger <= limite && !dispensadosIds.has(id)) {
                out.push({
                    id,
                    tipo: "vender-energia",
                    titulo: "Vender Energia",
                    venda: v,
                    triggerDate: trigger,
                    cor: "amber",
                });
            }
        }
        if (v.instalado && v.dataInstalacao) {
            [
                { meses: 2, t: "follow-2m", titulo: "Follow-up 2 meses", cor: "blue" },
                { meses: 4, t: "follow-4m", titulo: "Follow-up 4 meses", cor: "purple" },
            ].forEach(({ meses, t, titulo, cor }) => {
                const trigger = addMonths(new Date(v.dataInstalacao), meses);
                const id = `${v.id}::${t}`;
                if (trigger > hoje && trigger <= limite && !dispensadosIds.has(id)) {
                    out.push({ id, tipo: t, titulo, venda: v, triggerDate: trigger, cor });
                }
            });
        }
    });
    return out.sort((a, b) => a.triggerDate - b.triggerDate);
};

// ============ ICS (Calendar Export) ============

// ============ WhatsApp helpers ============

// Sanitiza telefone para formato wa.me (só dígitos, com indicativo internacional)
const sanitizePhone = (raw) => {
    if (!raw) return "";
    const digits = String(raw).replace(/[^\d+]/g, "").replace(/^00/, "+");
    let clean = digits.replace(/\+/g, "");
    // Se for só 9 dígitos a começar por 9/2/3 → é PT, prefixa 351
    if (clean.length === 9 && /^[239]/.test(clean)) clean = "351" + clean;
    return clean;
};

// Templates de mensagens
export const whatsappTemplates = {
    "follow-2m": (v) =>
        `Olá ${v.nome || ""}! Faz hoje 2 meses desde a sua adesão MEO. Está tudo a correr bem? Recebeu as faturas com os valores acordados?`,
    "follow-4m": (v) =>
        `Olá ${v.nome || ""}! Já lá vão 4 meses desde a sua adesão MEO. Está tudo a correr bem? Posso ajudar nalguma coisa?`,
    "vender-energia": (v) =>
        `Olá ${v.nome || ""}! Aqui da MEO. Tenho uma proposta de Energia que pode poupar bem na sua fatura. Posso explicar?`,
    default: (v) => `Olá ${v.nome || ""}, tudo bem?`,
};

// Constrói URL wa.me com mensagem opcional pré-preenchida
export const whatsappURL = (telefone, message = "") => {
    const phone = sanitizePhone(telefone);
    if (!phone) return "";
    const text = message ? `?text=${encodeURIComponent(message)}` : "";
    return `https://wa.me/${phone}${text}`;
};

// ============ ICS (Calendar Export) ============

const pad = (n) => String(n).padStart(2, "0");
const icsDate = (d) =>
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
const icsDateTime = (d) =>
    `${icsDate(d)}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
const icsEscape = (s) =>
    String(s || "").replace(/\\/g, "\\\\").replace(/,/g, "\\,").replace(/;/g, "\\;").replace(/\n/g, "\\n");

const buildICSEvent = ({ uid: u, summary, description, start, allDay = true, alarmMinutes = 60 * 24 }) => {
    const lines = [
        "BEGIN:VEVENT",
        `UID:${u}@salvador-controlo`,
        `DTSTAMP:${icsDateTime(new Date())}`,
    ];
    if (allDay) {
        lines.push(`DTSTART;VALUE=DATE:${icsDate(start)}`);
        lines.push(`DTEND;VALUE=DATE:${icsDate(addDays(start, 1))}`);
    } else {
        lines.push(`DTSTART:${icsDateTime(start)}`);
        lines.push(`DTEND:${icsDateTime(new Date(start.getTime() + 60 * 60 * 1000))}`);
    }
    lines.push(`SUMMARY:${icsEscape(summary)}`);
    if (description) lines.push(`DESCRIPTION:${icsEscape(description)}`);
    lines.push("BEGIN:VALARM");
    lines.push(`TRIGGER:-PT${alarmMinutes}M`);
    lines.push("ACTION:DISPLAY");
    lines.push(`DESCRIPTION:${icsEscape(summary)}`);
    lines.push("END:VALARM");
    lines.push("END:VEVENT");
    return lines.join("\r\n");
};

// Gera .ics para uma venda: instalação + alertas (15d, 2m, 4m)
export const vendaParaICS = (v) => {
    const events = [];
    if (v.dataInstalacao) {
        events.push(
            buildICSEvent({
                uid: `${v.id}-instal`,
                summary: `Instalação · ${v.nome}`,
                description: `${v.zona || ""} · ${v.vppNome || ""} · ${v.pacote} créditos${(v.planos || []).length ? " · " + v.planos.join(", ") : ""}`,
                start: new Date(v.dataInstalacao),
            }),
        );
    }
    if (v.dataVenda && !(v.planos || []).includes("Energia")) {
        events.push(
            buildICSEvent({
                uid: `${v.id}-energia`,
                summary: `🔌 Vender Energia · ${v.nome}`,
                description: "Cliente sem plano Energia — tentar venda complementar.",
                start: addDays(new Date(v.dataVenda), 15),
            }),
        );
    }
    if (v.instalado && v.dataInstalacao) {
        events.push(
            buildICSEvent({
                uid: `${v.id}-2m`,
                summary: `📞 Follow-up 2 meses · ${v.nome}`,
                description: "Confirmar se as faturas estão corretas e tudo a correr bem.",
                start: addMonths(new Date(v.dataInstalacao), 2),
            }),
        );
        events.push(
            buildICSEvent({
                uid: `${v.id}-4m`,
                summary: `📞 Follow-up 4 meses · ${v.nome}`,
                description: "Acompanhamento periódico.",
                start: addMonths(new Date(v.dataInstalacao), 4),
            }),
        );
    }
    return [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "PRODID:-//Salvador//Controlo//PT",
        "CALSCALE:GREGORIAN",
        "METHOD:PUBLISH",
        ...events,
        "END:VCALENDAR",
    ].join("\r\n");
};

// Gera .ics para todas as vendas (ou filtradas)
export const vendasParaICS = (vendas) => {
    const events = [];
    vendas.forEach((v) => {
        if (v.dataInstalacao) {
            events.push(
                buildICSEvent({
                    uid: `${v.id}-instal`,
                    summary: `Instalação · ${v.nome}`,
                    description: `${v.zona || ""} · ${v.vppNome || ""} · ${v.pacote} créd`,
                    start: new Date(v.dataInstalacao),
                }),
            );
        }
        if (v.instalado && v.dataInstalacao) {
            events.push(
                buildICSEvent({
                    uid: `${v.id}-2m`,
                    summary: `Follow-up 2m · ${v.nome}`,
                    description: "Confirmar faturação e estado.",
                    start: addMonths(new Date(v.dataInstalacao), 2),
                }),
            );
            events.push(
                buildICSEvent({
                    uid: `${v.id}-4m`,
                    summary: `Follow-up 4m · ${v.nome}`,
                    description: "Acompanhamento periódico.",
                    start: addMonths(new Date(v.dataInstalacao), 4),
                }),
            );
        }
    });
    return [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "PRODID:-//Salvador//Controlo//PT",
        "CALSCALE:GREGORIAN",
        "METHOD:PUBLISH",
        ...events,
        "END:VCALENDAR",
    ].join("\r\n");
};

export const downloadICS = (filename, ics) => {
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};
